import { Spinner } from "@repo/ui/components/Base/Spinner";
import {
  DataTable,
  DataTableWrapper,
} from "@repo/ui/components/Base/DataTable";
import { layoutDefaults, useColumns } from "./TeamsDataTableColumns";
import { EditTeamSheet } from "./EditTeamSheet";
import type { Team } from "@repo/types";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserColumns } from "@/hooks/useUserColumns";
import { KEYS } from "@/lib/constants";
import { ArchiveConfirmation } from "../dialog/ArchiveConfirmation";
import { TeamMembersTable } from "./teamMember/TeamMembersTable";
import { AddTeamMemberDialog } from "./teamMember/AddTeamMemberDialog";
import { PageHeader } from "../page";
import { TableCell } from "@repo/ui/components/Base/Table";
import { teamRepo } from "@/repositories/teamRepo";
import { useTeamList } from "@/hooks/queryHooks/teamHooks";

export const TeamsTable = ({ tenantId }: { tenantId: string }) => {
  const navigate = useNavigate();
  const { data: teams } = useTeamList(tenantId);

  const [activeTeam, setActiveTeam] = useState<Team>();
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { defaultState, saveTableLayout, resetTableLayout } = useUserColumns(
    KEYS.TEAM_TABLE_COLUMNS,
    layoutDefaults
  );

  const handleQuickEdit = (id: string) => {
    setEditSheetOpen(true);
    setActiveTeam(teams?.find((team) => team.id === id));
  };

  const handleEdit = (id: string) => {
    navigate(`${id}`);
  };

  const handleDelete = (id: string) => {
    setDeleteOpen(true);
    setActiveTeam(teams?.find((team) => team.id === id));
  };

  return (
    <>
      {!teams && <Spinner />}
      {activeTeam && (
        <EditTeamSheet
          open={editSheetOpen}
          setOpen={setEditSheetOpen}
          tenantId={tenantId}
          team={activeTeam}
        />
      )}
      {activeTeam && (
        <ArchiveConfirmation
          open={deleteOpen}
          setOpen={setDeleteOpen}
          itemId={activeTeam.id}
          onSubmit={async (teamId) => {
            await teamRepo.delete(tenantId, teamId);
          }}
          message={
            <>
              Deleting this team will remove all user privileges granted by it.
              <br />
              (Users not in another team will still be able to view the
              organization via "Full Organization.")
            </>
          }
        />
      )}

      <DataTable
        columns={useColumns({
          tenantId,
          handleQuickEdit,
          handleEdit,
          handleDelete,
        })}
        data={teams ?? []}
        filterColumn={{ label: "name", columnName: "name" }}
        userDefaults={defaultState}
        factoryDefaults={layoutDefaults}
        onLayoutSave={saveTableLayout}
        onLayoutReset={resetTableLayout}
        getRowCanExpand={() => true}
        renderSubComponent={({ row }) => (
          <TableCell colSpan={row.getVisibleCells().length} className="p-0">
            <div className="flex flex-col flex-grow bg-tertiary/20 p-2">
              <PageHeader title={row.original.name}>
                <AddTeamMemberDialog
                  tenantId={tenantId}
                  teamId={row.original.id}
                />
              </PageHeader>
              <DataTableWrapper>
                <TeamMembersTable
                  teamId={row.original.id}
                  tenantId={tenantId}
                />
              </DataTableWrapper>
            </div>
          </TableCell>
        )}
      />
    </>
  );
};
